// ─────────────────────────────────────────────
//  voiceIntent — pure parser for the ATC voice-control feature.
//
//  Turns a spoken/typed transcript into a structured intent the planner
//  (voicePlan.js) can turn into concrete, confirmable board actions.
//  Deterministic on purpose: it is the ALWAYS-WORKS layer. The optional
//  Claude-API callable (functions/voiceIntent.js) can produce richer intents;
//  when it is unavailable the app falls back to this parser transparently.
//
//  Intent shapes:
//    { type: "assign_crna",  roomIds: [id], roomNames: [name] }
//    { type: "help_rooms",   roomIds, roomNames }        // send help / overwhelmed
//    { type: "rotate_breaks" }
//    { type: "ratio_query" }
//    { type: "board_query" }
//    { type: "admin_report", text }                      // admin-only fallback
//    { type: "unknown", text }
// ─────────────────────────────────────────────

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Spoken numbers → digits ("or five" → "or 5").
const NUM_WORDS = {
  one: "1", two: "2", three: "3", four: "4", five: "5",
  six: "6", seven: "7", eight: "8", nine: "9", ten: "10",
  eleven: "11", twelve: "12",
};
const digitize = (s) => s.replace(
  new RegExp(`\\b(${Object.keys(NUM_WORDS).join("|")})\\b`, "g"),
  (w) => NUM_WORDS[w]
);

// Resolve every room mentioned in the transcript against the live rooms list.
// Speech gives "or 5", "o r 5", "room 3", "wor 2", "mri" — match by trailing
// number against each room's own trailing number, or by contained token
// ("mri" → "WC MRI", "tee" → "WC TEE", "ob" → "WC OB").
export function matchRooms(transcript, rooms = []) {
  const t = digitize(norm(transcript));
  const found = [];
  const seen = new Set();
  const add = (r) => { if (r && !seen.has(r.id)) { seen.add(r.id); found.push(r); } };

  // Numeric references: every standalone number, with the word spoken right
  // before it ("wor 5", "or 3", "cl 1"). Several rooms can share a trailing
  // number across areas (WOR 1 vs WC CL1) — rank candidates: a room whose own
  // name tokens include the preceding word wins; otherwise fall back to the
  // first candidate in board order.
  const numbers = [...t.matchAll(/(?:\b([a-z]{1,6})\s+)?\b(\d{1,2})\b/g)]
    .map((m) => ({ prefix: m[1] || "", n: m[2] }));
  for (const { prefix, n } of numbers) {
    const candidates = rooms.filter((r) => {
      const rn = String(r.name || "").match(/(\d{1,2})\s*$/);
      return rn && rn[1] === n;
    });
    if (!candidates.length) continue;
    const preferred =
      candidates.find((r) => prefix && norm(r.name).split(/[\s]+/).some((w) => w.replace(/\d+$/, "") === prefix || w === prefix)) ||
      // Generic "or"/"room" prefix: prefer the main-OR style name (contains
      // "or") over specialty areas.
      ((prefix === "or" || prefix === "room") &&
        candidates.find((r) => /\bw?or\b|\bor\s*\d/i.test(String(r.name)))) ||
      candidates[0];
    add(preferred);
  }
  // Named references (non-numeric rooms): match a room-name token in the text.
  for (const r of rooms) {
    const tokens = norm(r.name).split(" ").filter((w) => w.length >= 2 && !/^\d+$/.test(w));
    // Skip generic prefixes shared by every room ("wc", "or", "wor").
    const distinct = tokens.filter((w) => !["wc", "or", "wor", "room"].includes(w));
    if (distinct.some((w) => new RegExp(`\\b${w}\\b`).test(t))) add(r);
  }
  return found;
}

export function parseVoiceIntent(transcript, { rooms = [], isAdmin = false } = {}) {
  const raw = String(transcript || "").trim();
  const t = digitize(norm(raw));
  if (!t) return { type: "unknown", text: raw };

  const matched = matchRooms(raw, rooms);
  const roomIds = matched.map((r) => r.id);
  const roomNames = matched.map((r) => r.name);

  // Queries first (never mutate anything).
  if (/\b(supervision|ratio|supervis\w*)\b/.test(t)) {
    return { type: "ratio_query" };
  }
  if (/\b(status|board|looking like|what s (going on|happening)|overview)\b/.test(t) && !/\bhelp\b/.test(t)) {
    return { type: "board_query" };
  }

  // Break rotation.
  if (/\b(rotate|start|run)\b.*\bbreaks?\b/.test(t) || /\bbreaks?\b.*\b(rotation|rotate)\b/.test(t)) {
    return { type: "rotate_breaks" };
  }

  // Help / overwhelmed / need someone.
  if (/\b(help|overwhelm\w*|drowning|swamped|need (a |some )?(hand|help|relief|backup))\b/.test(t) && matched.length) {
    return { type: "help_rooms", roomIds, roomNames };
  }

  // Assign a CRNA / provider to a room.
  if (/\b(assign|send|put|add|give)\b.*\b(crna|provider|anesthetist|someone|aa)\b/.test(t) && matched.length) {
    return { type: "assign_crna", roomIds, roomNames };
  }
  // "send someone to OR 5" without the role word.
  if (/\b(assign|send|put)\b/.test(t) && matched.length) {
    return { type: "assign_crna", roomIds, roomNames };
  }
  // Rooms mentioned with distress but no verb we know — treat as help.
  if (matched.length && /\b(busy|behind|stat|now|asap)\b/.test(t)) {
    return { type: "help_rooms", roomIds, roomNames };
  }

  // Admin fallback: anything unrecognized from the admin becomes a structured
  // bug/issue report with the raw text preserved (the dev loop picks it up).
  if (isAdmin) return { type: "admin_report", text: raw, roomIds, roomNames };
  return { type: "unknown", text: raw };
}
